import { Button } from 'blaze-ui/Button'
import Styles from './BrandVisualStyleOptIn.module.scss'
import SharedStyles from '../BrandVisualStyleSelection/BrandVisualStyleSelection.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Images, Atom } from 'blaze-ui/icons/24'
import { Iphone02 } from 'blaze-ui/icons/16'
import { useUpdateBrandKit } from 'hooks/queries/brandKits/useUpdateBrandKit'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { VisualStyleInstagramPreview } from 'components/VisualStylePreview/VisualStyleInstagramPreview'
import { StyleGenerated } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleSelection/GenerationStage'
import cx from 'classnames'
import { useCreditUsage } from 'hooks/useCreditUsage'

export interface BrandVisualStyleOptInProps {
  onNext: (visualStylesOptIn: boolean) => void
  onBack: () => void
  brandKitId: number
  selectedStyle?: StyleGenerated
}

export const BrandVisualStyleOptIn = ({ onNext, onBack, brandKitId, selectedStyle }: BrandVisualStyleOptInProps) => {
  const { data: brandKitData } = useBrandKit(brandKitId)
  const { mutateAsync: updateBrandKit } = useUpdateBrandKit({ id: brandKitId })
  const { usageCosts } = useCreditUsage()
  const visualStylesCost = usageCosts?.visual_styles_credits_cost || 10

  const handleOptIn = (optIn: boolean) => {
    updateBrandKit(
      {
        content_preferences: {
          ...brandKitData?.brand_kit?.content_preferences,
          apply_visual_style: optIn,
        },
      },
      {
        onSuccess: () => onNext(optIn),
      },
    )
  }

  return (
    <div className={SharedStyles.selectImageStep}>
      <div className={SharedStyles.leftSide}>
        <div className={Styles.titleContainer}>
          <Heading level={1} color="var(--dark-90)">
            Do you want to use this Visual Style for all your content moving forward?
          </Heading>
        </div>

        <div className={cx(SharedStyles.content, Styles.content)}>
          <div className={Styles.mainContainer}>
            <div className={Styles.featureList}>
              <div className={Styles.featureItem}>
                <div className={Styles.featureIcon} style={{ background: '#f762be' }}>
                  <Images size={26} color="var(--light-100)" />
                </div>
                <Text variant="primary" color="var(--dark-90)">
                  Make all your photos look like they were shot by a top agency
                </Text>
              </div>

              <div className={Styles.featureItem}>
                <div className={Styles.featureIcon} style={{ background: '#28cedd' }}>
                  <Iphone02 size={26} color="var(--light-100)" />
                </div>
                <Text variant="primary" color="var(--dark-90)">
                  Any image, even from your phone, can be transformed instantly
                </Text>
              </div>

              <div className={Styles.featureItem}>
                <div className={Styles.featureIcon} style={{ background: '#ffae00' }}>
                  <Atom size={26} color="var(--light-100)" />
                </div>
                <Text variant="primary" color="var(--dark-90)">
                  Generate unlimited amount of content from your photos
                </Text>
              </div>
            </div>

            <div className={Styles.primaryOptionContainer}>
              <Button
                variant="primary"
                size="xl"
                onPress={() => handleOptIn(true)}
                aria-label="Yes, apply this style to my images!"
              >
                Yes, apply this style to my images!
              </Button>
              <Text variant="secondary" color="var(--dark-40)">
                This would cost only {visualStylesCost} credits per generation.
              </Text>
            </div>
          </div>

          <div className={Styles.secondaryOptionContainer}>
            <Button
              variant="secondary"
              size="lg"
              onPress={() => handleOptIn(false)}
              aria-label="No, I want to keep using my images as they are"
            >
              No, I want to keep using my images as they are
            </Button>
            <Text variant="secondary" color="var(--dark-90)">
              Ideal for brands with significant amounts of professionally shot content and a full-time design team to
              make edits.
            </Text>
          </div>
        </div>

        <div className={SharedStyles.footer}>
          <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back">
            Back
          </Button>
          <div />
        </div>
      </div>

      <div className={cx(SharedStyles.transformedImageContainer, Styles.transformedImageContainer)}>
        {selectedStyle && (
          <VisualStyleInstagramPreview
            imageUrl={selectedStyle.styleUrl}
            accountName={brandKitData?.brand_kit?.brand_profile?.name || 'Your Account'}
            avatarUrl={brandKitData?.brand_kit?.brand_visual_kit?.primary_logo?.url}
            brandKitId={brandKitId}
            brandVoiceId={brandKitData?.brand_kit?.brand_voice?.id}
            brandKitName={brandKitData?.brand_kit?.name}
          />
        )}
      </div>
    </div>
  )
}
